import * as Sentry from '@sentry/react';
import { UseQueryResult, useQuery } from '@tanstack/react-query';
import {
  OrderPipelineAuditResponse,
  getOrderPipelineAudit,
} from 'api/admin/orders/getOrderPipelineAudit';

/**
 * Pipeline history for one order.
 *
 * The endpoint reads the DataDog Logs API, whose budget is 300 requests per minute org-wide (shared
 * with dashboards and monitors) — ample for operator traffic. So the caution here is about not
 * surprising the user rather than conserving requests: no polling and no refetch on window focus,
 * but a short `staleTime` so revisiting the page or hitting Refresh actually re-reads. A long
 * `staleTime` would hurt the main use case — adjust an order, open the timeline, see what happened.
 */
export const useOrderPipelineAudit = (
  serviceOrderId: string | number | undefined,
): UseQueryResult<OrderPipelineAuditResponse, unknown> => {
  return useQuery({
    queryKey: ['orderPipelineAudit', String(serviceOrderId)],
    queryFn: async () => {
      /*
       * Unreachable — `enabled` gates the query on the same value. Narrowing by guard rather than by
       * cast keeps the id honest: if this ever did fire it surfaces as the panel's error state,
       * instead of requesting `/service-orders/undefined/pipeline-audit`.
       */
      if (serviceOrderId === undefined) {
        throw new Error('useOrderPipelineAudit called without a service order id');
      }

      const { data } = await getOrderPipelineAudit(serviceOrderId);
      return data;
    },
    enabled: Boolean(serviceOrderId),
    onError: (error: unknown) => {
      Sentry.captureException(error);
    },
    retry: 1,
    refetchOnWindowFocus: false,
    staleTime: 30 * 1000,
  });
};
